import type { ReactNode } from 'react'
import { AlertCircle, Inbox, RefreshCw } from 'lucide-react'

import { Button, type Icon } from '@/components/ui/Controls'
import { ApiError, errorTitle, isRetryable } from '@/lib/api/errors'
import { cn } from '@/lib/utils/cn'

/**
 * Loading, empty and error states.
 *
 * Every data-dependent view uses these three, so no screen is ever blank while a request
 * is in flight and no failure is ever silent.
 */

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-raised', className)} aria-hidden="true" />
}

export function TableSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-px p-3" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-3 py-1.5">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-3.5', columnIndex === 1 ? 'flex-[2]' : 'flex-1')}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * An empty screen is an invitation to act, so every empty state says what to do next
 * rather than only reporting absence.
 */
export function EmptyState({
  title,
  hint,
  action,
  className,
  icon: IconComponent = Inbox,
}: {
  title: string
  hint?: ReactNode
  action?: ReactNode
  className?: string
  icon?: Icon
}) {
  return (
    <div className={cn('flex flex-col items-center gap-2 px-6 py-14 text-center', className)}>
      <span
        aria-hidden="true"
        className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-primary-wash
          text-primary"
      >
        <IconComponent className="h-5 w-5" />
      </span>
      <p className="text-md font-semibold text-ink">{title}</p>
      {hint ? <p className="max-w-md text-sm text-ink-muted">{hint}</p> : null}
      {action ? <div className="pt-3">{action}</div> : null}
    </div>
  )
}

/**
 * Errors explain what happened and how to fix it, in the interface's voice.
 * The backend already produces actionable messages; this renders them faithfully and adds
 * the request id, which is what makes a support conversation possible.
 */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown
  onRetry?: () => void
  className?: string
}) {
  const apiError = error instanceof ApiError ? error : null
  const title = apiError ? errorTitle(apiError) : 'Something went wrong'
  const message =
    apiError?.message ??
    (error instanceof Error ? error.message : 'The request failed for an unknown reason.')

  return (
    <div className={cn('flex flex-col items-start gap-3 px-6 py-10', className)} role="alert">
      <div className="flex items-center gap-2">
        <AlertCircle className="h-5 w-5 text-fail" aria-hidden="true" />
        <p className="text-md font-semibold text-ink">{title}</p>
      </div>
      <p className="max-w-2xl text-sm text-ink-dim">{message}</p>
      <div className="flex items-center gap-4">
        {onRetry && (!apiError || isRetryable(apiError)) ? (
          <Button size="sm" icon={RefreshCw} onClick={onRetry}>
            Try again
          </Button>
        ) : null}
        {apiError?.requestId ? (
          <span className="mono text-ink-faint">request {apiError.requestId}</span>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The three states, resolved in one place so no view forgets one.
 *
 * `children` may be a function. That matters: JSX passed as a child is evaluated *before*
 * this component decides what to render, so a success-path child that dereferences
 * `data!` crashes on the very first render, while the request is still in flight. Passing
 * a function defers evaluation until the data actually exists.
 */
export function QueryBoundary({
  isLoading,
  error,
  isEmpty,
  onRetry,
  loadingFallback,
  emptyState,
  children,
}: {
  isLoading: boolean
  error: unknown
  isEmpty?: boolean
  onRetry?: () => void
  loadingFallback?: ReactNode
  emptyState?: ReactNode
  children: ReactNode | (() => ReactNode)
}) {
  if (isLoading) return <>{loadingFallback ?? <TableSkeleton />}</>
  if (error) return <ErrorState error={error} onRetry={onRetry} />
  if (isEmpty) return <>{emptyState ?? <EmptyState title="Nothing to show yet." />}</>
  return <>{typeof children === 'function' ? children() : children}</>
}
